import React, { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import api, { formatApiError } from "../lib/api";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger, DialogFooter,
} from "@/components/ui/dialog";
import { Layout, Plus, ExternalLink, BarChart3, Eye, Loader2, Trash2, Globe } from "lucide-react";
import { toast } from "sonner";

export default function Funnels() {
  const { activeSubId } = useAuth();
  const navigate = useNavigate();
  const [funnels, setFunnels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [name, setName] = useState("");
  const [saving, setSaving] = useState(false);

  const load = useCallback(() => {
    if (!activeSubId) return;
    setLoading(true);
    api.get(`/funnels?sub_account_id=${activeSubId}`).then(({ data }) => { setFunnels(data); setLoading(false); });
  }, [activeSubId]);
  useEffect(() => { load(); }, [load]);

  const create = async () => {
    setSaving(true);
    try {
      const { data } = await api.post(`/funnels?sub_account_id=${activeSubId}`, { name });
      toast.success("Funnel created");
      setOpen(false); setName("");
      navigate(`/funnels/${data.id}`);
    } catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    finally { setSaving(false); }
  };

  const del = async (id, e) => {
    e.stopPropagation();
    await api.delete(`/funnels/${id}`); toast.success("Funnel deleted"); load();
  };

  const publicUrl = (id, slug) => `${window.location.origin}/f/${id}`;

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>;

  return (
    <div className="space-y-6 animate-fade-up">
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs uppercase tracking-[0.18em] text-muted-foreground font-semibold">Funnels & Pages</p>
          <h1 className="text-3xl sm:text-4xl font-display font-bold tracking-tight mt-1">Funnels</h1>
        </div>
        <Dialog open={open} onOpenChange={setOpen}>
          <DialogTrigger asChild><Button className="gap-2 font-semibold" data-testid="new-funnel-button"><Plus className="w-4 h-4" />New funnel</Button></DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New funnel</DialogTitle></DialogHeader>
            <div className="space-y-1.5 py-2"><Label>Funnel name</Label>
              <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Free Training Funnel" data-testid="funnel-name-input" /></div>
            <DialogFooter><Button onClick={create} disabled={saving || !name} data-testid="save-funnel-button">Create & open builder</Button></DialogFooter>
          </DialogContent>
        </Dialog>
      </div>

      {funnels.length === 0 ? (
        <Card className="p-16 text-center bg-card border-border/70" data-testid="funnels-empty">
          <Layout className="w-10 h-10 text-muted-foreground mx-auto mb-4" />
          <p className="text-muted-foreground">No funnels yet. Create one or install a snapshot to get a pre-built funnel.</p>
        </Card>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {funnels.map((f) => (
            <Card key={f.id} className="p-6 bg-card border-border/70 hover:border-primary/40 transition-colors cursor-pointer group"
              onClick={() => navigate(`/funnels/${f.id}`)} data-testid={`funnel-card-${f.id}`}>
              <div className="flex items-start justify-between">
                <div className="w-11 h-11 rounded-xl bg-primary/15 flex items-center justify-center"><Layout className="w-5 h-5 text-primary" /></div>
                <Badge variant="secondary" className={f.status === "published" ? "text-emerald-400" : "text-muted-foreground"}>
                  {f.status === "published" ? "Live" : "Draft"}
                </Badge>
              </div>
              <p className="font-display font-semibold text-lg mt-4 group-hover:text-primary transition-colors">{f.name}</p>
              <p className="text-xs text-muted-foreground mt-0.5">{f.pages?.length || 0} pages</p>
              <div className="flex items-center gap-4 mt-4 text-sm">
                <span className="text-muted-foreground">Visits <span className="font-semibold text-foreground">{f.visits}</span></span>
                <span className="text-muted-foreground">Opt-ins <span className="font-semibold text-foreground">{f.submissions}</span></span>
              </div>
              <div className="flex gap-2 mt-5 pt-4 border-t border-border/60">
                {f.status === "published" && (
                  <a href={publicUrl(f.id)} target="_blank" rel="noreferrer" onClick={(e) => e.stopPropagation()}
                    className="flex-1 inline-flex items-center justify-center gap-1.5 h-9 rounded-md border border-border text-sm hover:bg-secondary/50 transition-colors" data-testid={`view-live-${f.id}`}>
                    <Globe className="w-3.5 h-3.5" /> View live
                  </a>
                )}
                <button onClick={(e) => del(f.id, e)} className="h-9 px-3 rounded-md border border-border text-muted-foreground hover:text-destructive transition-colors" data-testid={`delete-funnel-${f.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
